// Starting a mission RUN — the explicit door (CCT-286).
//
// Two calls, and the order matters:
//   1. which languages this mission can be PLAYED in (public: the choice happens before, and
//      possibly without, a session);
//   2. `start`, which FREEZES that choice on the run and guarantees the mission's engine.
//
// ⚠️ `start` IS NOT A NAVIGATION DETAIL. Until it is called, the engine may be stopped, no user
// story is ever materialised, and the task screen correctly reports that the server does not
// know the task — on a mission the player can otherwise browse. That divergence is exactly what
// this endpoint exists to close, so every launch path must go through here.
import { apiFetch, ApiError } from './client';

export interface PlayLanguages {
  missionId: number;
  languages: string[];
  default: string;
}

export interface StartedRun {
  started: boolean;
  reason?: string;
  /** Natures à activer quand `reason === 'require_controllers'` — le refus se DIT, il ne se devine pas. */
  controllers?: string[];
  /** Le message du serveur, qui nomme ce qui manque et pourquoi. Repli si la clé est inconnue. */
  message?: string;
  /** CCT-400 : la CLÉ du message, que l'app rend dans la langue du joueur. */
  messageKey?: string;
  missionId?: number;
  language?: string;
  /** True only on the call that actually froze the language — lets the UI say so once. */
  languageJustFrozen?: boolean;
  /** Whether the engine is running afterwards. */
  engine?: boolean;
  /** True when this call WIPED the previous run to start over in another language. */
  restarted?: boolean;
}

/** State of THIS player's run — read-only, freezes nothing. */
export interface MissionRun {
  enrolled: boolean;
  /**
   * La partie est-elle RÉELLEMENT lancée ? Tranché par le serveur.
   *
   * ⚠️ NE PAS LE REDÉDUIRE ICI. Le premier garde déduisait « démarrée » de « une langue est
   * figée » et laissait donc passer une partie dont `status` valait « not_started » — mesuré
   * sur la mission 1 : `language = 'en'`, `status = 'not_started'`, et l'écran rendait le
   * chapitre. Deux définitions du même fait finissent toujours par diverger.
   */
  started?: boolean;
  /**
   * Does this mission's engine still need a wake-up?
   *
   * ⚠️ DECIDED BY THE SERVER, NEVER RE-DERIVED HERE. It takes the nudge setting AND the job
   * status; the client has neither. `false` allows opening a running game WITHOUT reposting the
   * start, which avoids reloading the whole scenario to re-plan a voice already planned. Absent
   * means `true`: when in doubt, restart.
   */
  engineWakeNeeded?: boolean;
  /** Language the run is frozen in, `null` while nothing has started. */
  language: string | null;
  progress: number;
  /**
   * Éléments RÉELLEMENT terminés — c'est ce que « relancer » efface.
   *
   * ⚠️ `progress` NE LE DIT PAS. Mesuré sur une partie affichant 12/12 : `progress` valait 0 et
   * `status` « not_started », alors que 22 éléments étaient faits. Annoncer « 0 % » avant une
   * suppression irréversible est pire que ne rien annoncer.
   */
  completedItems?: number;
  status?: string | null;
  isTrial?: boolean;
  /**
   * May this person MANAGE the mission (its author, an admin of its company, a global admin)?
   *
   * ⚠️ DECIDED BY THE SERVER, AND ONLY DISPLAYED HERE. It unlocks two destructive buttons and a
   * bypass of the licence: re-deriving it client-side would mean shipping the rule twice, and the
   * copy that drifts is always the one that opens the door too wide. Absent means `false` — an
   * older server that cannot answer must not grant management.
   */
  canManage?: boolean;
}

/** Reload the mission from the top. `controllers` ALSO forgets the granted accesses. */
export async function managerReset(missionId: number, controllers: boolean,
                                   token?: string | null): Promise<MissionRun | null> {
  // ⚠️ WE INVALIDATE, WE DO NOT DEDUCE. The server resets the run and opens the enrolment if
  // needed; rewriting that state from memory here would create a second definition of one fact.
  const r = await apiFetch<MissionRun>(
    `/me/stories/${missionId}/manager-reset?controllers=${controllers ? "true" : "false"}`,
    { method: "POST", token, auth: true });
  oublierEtatPartie(missionId);
  return r ?? null;
}

// --- État partagé de la partie, par mission — CCT-291 ------------------------
//
// ⚠️ DEUX LECTEURS INDÉPENDANTS DU MÊME FAIT, ET ON LE PAYAIT EN RÉSEAU. Mesuré à l'ouverture
// d'un chapitre : `/me/stories/{id}/run` appelé DEUX fois en 480 ms — une fois par
// `useMissionLaunch` avant de proposer la langue, une fois par la garde de
// `ChapterDetailScreen`. Deux définitions du même fait finissent toujours par diverger ; ici
// elles divergeaient dans le temps, ce qui est la version bénigne — mais c'est la même faute.
//
// Trois mécanismes, et chacun couvre un cas que les autres ne couvrent pas :
//
//   1. `enVol` — deux composants montés EN MÊME TEMPS partagent la même promesse ;
//   2. `connu` — un état déjà lu, valable brièvement, évite la relecture immédiate ;
//   3. `noterDemarrage` — la réponse d'un `start` DIT l'état d'après. La relire serait demander
//      au serveur ce qu'il vient de répondre. C'est ce troisième point qui supprime réellement
//      le doublon mesuré, les deux appels n'étant pas simultanés mais séparés par le `start`.
//
// ⚠️ ET RIEN NE PEUT RESTER COINCÉ. L'entrée `enVol` est retirée dans un `finally` — donc aussi
// quand la requête échoue. Sans cela, un échec réseau laisserait une promesse rejetée en place
// et TOUT appel ultérieur pour cette mission recevrait indéfiniment ce même échec.

/** Durée de validité d'un état lu. Assez pour couvrir une ouverture d'écran, trop court pour
 *  servir un état périmé après une action du joueur. Les écritures invalident de toute façon. */
const FRAICHEUR_MS = 5000;

interface Entree { valeur: MissionRun | null; a: number }

const connu = new Map<number, Entree>();
const enVol = new Map<number, Promise<MissionRun | null>>();

const maintenant = (): number => Date.now();

/** Oublie l'état d'une mission — ou de toutes (déconnexion, tests). */
export function oublierEtatPartie(missionId?: number): void {
  if (missionId === undefined) { connu.clear(); enVol.clear(); return; }
  connu.delete(missionId);
  enVol.delete(missionId);
}

/**
 * Enregistre ce qu'une ÉCRITURE vient d'établir, sans relire.
 *
 * ⚠️ C'EST LA RÉPONSE DU SERVEUR, PAS UNE DÉDUCTION. `start` répond `started` et la langue
 * figée : ce sont des post-conditions, pas des suppositions du client. Les champs qu'elle ne
 * mentionne pas (progression, essai) gardent leur dernière valeur connue.
 */
export function noterDemarrage(missionId: number, r: StartedRun): void {
  if (!r?.started) return;   // un démarrage refusé n'établit rien
  const precedent = connu.get(missionId)?.valeur ?? null;
  connu.set(missionId, {
    a: maintenant(),
    valeur: {
      ...(precedent ?? { enrolled: true, language: null, progress: 0 }),
      started: true,
      language: r.language ?? precedent?.language ?? null,
      // Relancer efface la progression : la garder afficherait un chiffre que le serveur vient
      // de remettre à zéro.
      ...(r.restarted ? { progress: 0, completedItems: 0 } : {}),
    },
  });
}

/**
 * Reads the player's run before offering anything.
 *
 * ⚠️ WITHOUT THIS, THE LAUNCH SCREEN ASKS BLIND — AND THAT IS THE BUG THE PLAYER REPORTED. The
 * language list is PUBLIC (the choice precedes enrolment), so it cannot know that a run is
 * already going, nor in which language. The app therefore asked the same question on the first
 * open and on the twelfth, and the server ignored the answer every time after the first: picking
 * English left the mission in French, silently. A question whose answer is discarded is worse
 * than no question.
 */
export async function fetchMissionRun(
  token: string | null, missionId: number, options: { force?: boolean } = {},
): Promise<MissionRun | null> {
  if (!options.force) {
    const e = connu.get(missionId);
    if (e && maintenant() - e.a < FRAICHEUR_MS) return e.valeur;
    const partagee = enVol.get(missionId);
    if (partagee) return partagee;      // un autre composant charge déjà : on attend LA MÊME
  }
  const p = (async () => {
    try {
      const r = await apiFetch(`/me/stories/${missionId}/run`, { token, auth: true });
      connu.set(missionId, { valeur: r as MissionRun, a: maintenant() });
      return r as MissionRun;
    } catch {
      // ⚠️ UN SERVEUR MUET NE BLOQUE PAS, ET NE SE MET PAS EN CACHE. Rendre `null` laisse les
      // gardes continuer ; le mémoriser ferait passer une panne réseau d'une seconde pour un
      // « pas de partie » pendant les cinq suivantes.
      return null;
    }
  })();
  // ⚠️ RETIRÉ DANS UN `finally`, DONC AUSSI EN CAS D'ÉCHEC. Une promesse laissée dans la carte
  // servirait son résultat — succès périmé ou rejet — à tous les appels suivants de cette
  // mission, indéfiniment. C'est la fuite que cette carte pourrait introduire, et la seule.
  enVol.set(missionId, p);
  try {
    return await p;
  } finally {
    if (enVol.get(missionId) === p) enVol.delete(missionId);
  }
}

/** Combien de requêtes `/run` sont en vol — pour les tests, et pour rien d'autre. */
export function enVolPourTests(): number {
  return enVol.size;
}

/**
 * Languages this mission can be played in.
 *
 * ⚠️ THE SERVER IS THE AUTHORITY, NOT THE CARD. The catalogue chips and this list come from the
 * same rule server-side, but the card may be stale in a list loaded minutes ago. Asking again at
 * launch avoids offering a language that has since been judged non-existent — and the choice is
 * FROZEN, so an offer that is wrong would stick for the whole run.
 */
export async function fetchPlayLanguages(missionId: number): Promise<PlayLanguages | null> {
  try {
    return await apiFetch(`/public/missions/${missionId}/languages`);
  } catch {
    return null;
  }
}

/**
 * Starts (or resumes) this player's run.
 *
 * Idempotent: called again on a run already started, it changes neither the language nor the
 * engine. Safe to call on every launch, which is what makes it usable from several screens.
 */
export async function startMissionRun(
  token: string | null,
  missionId: number,
  language?: string | null,
  restart = false,
): Promise<StartedRun> {
  // ⚠️ `restart` EFFACE LA PROGRESSION DU JOUEUR. Il n'est jamais déduit d'une divergence de
  // langue : seul un geste explicite, avec la perte annoncée à l'écran, le met à vrai.
  const params = new URLSearchParams();
  if (language) params.set('language', language);
  if (restart) params.set('restart', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  try {
    const r: StartedRun = await apiFetch(`/me/stories/${missionId}/start${query}`, {
      method: 'POST', token, auth: true,
    });
    // La réponse DIT l'état d'après : on le note au lieu de le redemander. C'est ce qui
    // supprime le second `/run` mesuré à l'ouverture d'un chapitre.
    noterDemarrage(missionId, r);
    return r;
  } catch (e) {
    // ⚠️ UN REFUS N'EST PAS UNE PANNE, ET LES CONFONDRE ANNULAIT LA GARDE. Le serveur refuse le
    // démarrage tant que les contrôleurs ne sont pas activés (403 `require_controllers`,
    // CCT-328) ; ce `catch` rangeait ce refus avec les coupures réseau et laissait entrer le
    // joueur sans un mot. Mesuré sur la mission 24 : le serveur répondait BLOQUE, l'app ouvrait
    // quand même. La garde vivait, mais personne ne l'écoutait.
    const detail = (e instanceof ApiError && e.status === 403
      ? (e.body as { detail?: { error?: string; controllers?: string[]; message?: string;
                                messageKey?: string } })?.detail
      : undefined);
    if (detail?.error === 'require_controllers') {
      // CCT-400: `messageKey` travels so the sentence is rendered in the player's
      // language; `message` is the server's English, kept for an older runtime.
      return { started: false, reason: 'require_controllers',
               controllers: detail.controllers ?? [], message: detail.message,
               messageKey: detail.messageKey };
    }
    // ⚠️ NE JAMAIS BLOQUER LA NAVIGATION SUR UNE PANNE. Le joueur doit pouvoir entrer dans sa
    // mission si le démarrage a échoué pour une autre raison : le contenu est lisible, et le
    // panneau de statut dira honnêtement que le moteur ne tourne pas.
    return { started: false, reason: 'request_failed' };
  }
}

/**
 * Le joueur CLÔT sa partie.
 *
 * ⚠️ LE BOUTON EXISTAIT DEPUIS TOUJOURS ET N'APPELAIT RIEN. « ABANDONNER L'OPÉRATION » faisait
 * `navigation.goBack()` : depuis une scène, cela ramène au chapitre, si bien que l'action la
 * plus engageante de l'écran donnait l'impression de ne rien faire. Elle ne faisait rien.
 *
 * N'efface PAS la progression : abandonner n'est pas relancer. Le prochain démarrage repart de
 * zéro et rouvre le choix de la langue, puisque c'est une nouvelle partie.
 */
export async function abandonMissionRun(
  token: string | null, missionId: number,
): Promise<{ abandoned: boolean; reason?: string }> {
  try {
    const r = await apiFetch(`/me/stories/${missionId}/abandon`, {
      method: 'POST', token, auth: true,
    });
    // ⚠️ ON INVALIDE, ON NE DÉDUIT PAS. Abandonner remet la partie à « non démarrée » et libère
    // la langue ; écrire cet état de tête ici recréerait une deuxième définition du même fait —
    // exactement ce que ce module vient de supprimer. La prochaine lecture ira au serveur.
    oublierEtatPartie(missionId);
    return r;
  } catch {
    // ⚠️ ON NE MENT PAS SUR L'ÉCHEC. Rendre `{abandoned: true}` par confort ramènerait le
    // joueur à la liste en lui laissant croire que sa partie est close — elle ne le serait pas.
    return { abandoned: false, reason: 'request_failed' };
  }
}

// --- Ce que la mission EXIGE avant d'être jouée — CCT-328 --------------------

export interface MissionController {
  kind: string;
  ready: boolean;
  detail?: string;
  mode?: string;
}

export interface MissionControllers {
  controllers: MissionController[];
  allReady: boolean;
}

/**
 * État des contrôleurs d'une mission pour ce joueur.
 *
 * ⚠️ CE N'EST PAS LA GARDE, C'EST L'ANNONCE. Le serveur refuse déjà de démarrer une mission dont
 * un contrôleur manque ; cette lecture existe pour le DIRE avant le clic, au lieu de laisser
 * remonter un refus brut. Une garde qui ne vivrait qu'ici n'en serait pas une : l'appel de
 * démarrage est une route de l'API, joignable sans passer par cet écran.
 */
export async function fetchMissionControllers(
  token: string | null, missionId: number,
): Promise<MissionControllers | null> {
  if (!token) return null;
  try {
    return await apiFetch<MissionControllers>(`/me/missions/${missionId}/controllers`, {
      token, auth: true, background: true,
    });
  } catch {
    // Illisible : l'appelant laisse le serveur trancher plutôt que d'inventer un verdict.
    return null;
  }
}
